import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import Button from '../../../ui/button'
import MessageArea from '../../../../pages/payment/materials/MessageArea'
import RadioGroupField from '../../../form/RadioGroupField'

import { createLocalizedPath } from '../../../../utils/localizationHelpers'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { RootState } from '../../../../store/rootReducer'
import { Field, FieldProps, Form, Formik, FormikProps } from 'formik'
import { SelectOption } from '../../../../store/commonTypes'
import { setPageToReturnFromPayment } from '../../../../store/common/commonSlice'
import { giftSave } from '../../../../api/gifts'
import paths from '../../../../paths'
import './giveVirtualGiftPopup.sass'

const visibilityOptions: SelectOption[] = [
    {
        value: 'all',
        label: 'All'
    },
    {
        value: 'recipient',
        label: 'Recipient only'
    },
    {
        value: 'nobody',
        label: 'Nobody'
    }
]

const GiveVirtualGiftPopup = ({ gift, recipient, externalState = false, setExternalState }: any) => {
    const [t] = useTranslation()
    const history = useHistory()
    const location = useLocation()
    const dispatch = useDispatch()

    const { user } = useSelector((state: RootState) => state.member)

    const [attachmentSrc, setAttachmentSrc] = useState(null)
    const inputFileRef = useRef(null)
    const [pm, setPm] = useState('')
    const [isFaqOpened, setIsFaqOpened] = useState(false)

    const loadedImageUrl = (form: FormikProps<any>) => {
        const reader = new FileReader()
        reader.readAsDataURL(inputFileRef.current.files[0])
        reader.onload = function () {
            setAttachmentSrc(reader.result)
            form.setFieldValue('image_data', reader.result)
        }
    }

    const valuesToRequest = (nick?: string, save?: number) => {
        return {
            id: gift.id || null,
            gift_id: gift.gift_id,
            recipient,
            comment: pm,
            anon: nick ? visibilityOptions.findIndex((item) => item.value === nick) : 0,
            save,
            image_data: attachmentSrc || ''
        }
    }

    const handleSubmit = (nick: string, save = 0) =>
        giftSave(valuesToRequest(nick, save)).then((res) => {
            switch (res.result) {
                case 'gift_saved':
                    setExternalState(false)
                    history.push(createLocalizedPath(paths.myWallet.path))
                    break
                case 'payment_required':
                    dispatch(setPageToReturnFromPayment(paths.myWallet.path + '?id=' + res.id))
                    history.push({
                        pathname: createLocalizedPath(paths.payment.path),
                        search: `?tokens`
                    })
                    break
                case 'gift_sent':
                    history.push(paths.profileGifts.toPath({ nick: recipient }))
                    break
                default:
                    break
            }
        })

    const handleClosePopup = () => {
        if (location.pathname.includes('membre/wallet')) history.replace(createLocalizedPath(paths.myWallet.path))
        setExternalState(false)
    }

    useEffect(() => {
        if (gift.comment) setPm(gift.comment)
    }, [])

    if (externalState)
        return (
            <Formik
                initialValues={{
                    image_data: null,
                    nick_expose: 'all',
                    comment: ''
                }}
                onSubmit={(values) => handleSubmit(values.nick_expose)}
            >
                <Form className={'formAbsolute'}>
                    <div className={'fullscreenWrapper'} onClick={handleClosePopup}>
                        <div className={'modal-dialog'} onClick={(e) => e.stopPropagation()}>
                            <p className={'popupHeader'}>{t('Give a virtual gift')}</p>
                            <div className={'halfWrapper'}>
                                <div className="left">
                                    <img src={gift.image} />
                                </div>
                                <div className="right">
                                    <p>{gift.gift_name}</p>
                                    <p>{gift.points + ' ' + t('points')}</p>
                                    <Button label={t('Give this virtual gift')} type={'submit'} />
                                    <Button
                                        label={t('Put on hold')}
                                        secondary
                                        type={'button'}
                                        onClick={() => handleSubmit('', 1)}
                                    />
                                    <p>
                                        {t('From')}
                                        {user && (
                                            <Link
                                                to={{ pathname: createLocalizedPath(`/profil/${user.nick}`) }}
                                                className={'nickLink'}
                                                target="_blank"
                                            >
                                                {user.nick}
                                            </Link>
                                        )}
                                    </p>
                                    <p>
                                        {t('To')}
                                        <Link
                                            to={{ pathname: createLocalizedPath(`/profil/${recipient}`) }}
                                            className={'nickLink'}
                                            target="_blank"
                                        >
                                            {recipient}
                                        </Link>
                                    </p>
                                </div>
                            </div>
                            <div className={'halfWrapper'}>
                                <div className="left">
                                    <p>{t('Attachment')}</p>
                                    {(attachmentSrc || gift.attachment_image) && (
                                        <img
                                            src={attachmentSrc || gift.attachment_image}
                                            className="attachment-preview"
                                        />
                                    )}
                                    <Field name="image_data">
                                        {({ form }: FieldProps) => (
                                            <input
                                                type="file"
                                                name="attachment"
                                                accept="image/jpeg"
                                                ref={inputFileRef}
                                                onChange={() => loadedImageUrl(form)}
                                            />
                                        )}
                                    </Field>
                                    <Button
                                        label={t('Choose file')}
                                        secondary
                                        onClick={() => inputFileRef.current.click()}
                                        type={'button'}
                                    />
                                </div>
                                <div className="right">
                                    <p>{t('Expose you nick to')}</p>
                                    <Field name="nick_expose">
                                        {({ field, form }: FieldProps) => (
                                            <RadioGroupField
                                                form={form}
                                                field={field}
                                                label={''}
                                                options={visibilityOptions}
                                            />
                                        )}
                                    </Field>
                                </div>
                            </div>
                            <MessageArea externalState={pm} setExternalState={setPm} label={t('PM gift')} />
                            <div className={'giftFAQ'}>
                                <div className={'giftFAQ-firstLine'} onClick={() => setIsFaqOpened(!isFaqOpened)}>
                                    <p>FAQ</p>
                                    <p>{isFaqOpened ? '-' : '+'}</p>
                                </div>
                                {isFaqOpened && (
                                    <div className={'giftFAQ-description'}>
                                        <p>{t('By giving this virtual gift')}</p>
                                        <p>{t('You can attach a message')}</p>
                                        <p>{t('Your virtual gift')}</p>
                                        <p>{t('You can choose between 3 options')}</p>
                                        <p>{t('Show your nickname to')}</p>
                                        <p>{t('Your nickname will be visible')}</p>
                                        <p>{t('Show your nickname to recipient')}</p>
                                        <p>{t('Your nickname will be visible only to the recipient')}</p>
                                        <p>{t('Show your nickname to nobody')}</p>
                                        <p>{t('Your nickname will not be visible')}</p>
                                        <p>{t('Your virtual gift will be anonymous')}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div
                            className={'closeButton'}
                            onClick={(e) => {
                                e.stopPropagation()
                                handleClosePopup()
                            }}
                        />
                    </div>
                </Form>
            </Formik>
        )
    return <></>
}

export default GiveVirtualGiftPopup
